import type {
  PromptResponse,
  PromptListResponse,
  PromptVersionsResponse,
  CreatePromptVersionRequest,
  TestPromptRequest,
  TestPromptResponse,
  ExecutionResponse,
  ExecutionListResponse,
} from './types'
import { API_CONFIG } from '@/shared/config'

const API_URL = API_CONFIG.baseUrl

async function getAuthHeaders(): Promise<HeadersInit> {
  const { getAuthToken } = await import('@trafiflow/shared-ui')
  const token = await getAuthToken()
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  }
}

export const promptEngineeringService = {
  /**
   * List all prompts (latest active version of each).
   */
  async listPrompts(): Promise<PromptListResponse> {
    const headers = await getAuthHeaders()

    const response = await fetch(`${API_URL}/prompts`, {
      method: 'GET',
      headers,
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(errorData.error || `Failed to list prompts: ${response.status}`)
    }

    return response.json()
  },

  /**
   * Get a prompt with all its versions.
   */
  async getPrompt(promptId: string): Promise<PromptVersionsResponse> {
    const headers = await getAuthHeaders()
    const encodedId = encodeURIComponent(promptId)

    const response = await fetch(`${API_URL}/prompts/${encodedId}`, {
      method: 'GET',
      headers,
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(errorData.error || `Failed to get prompt: ${response.status}`)
    }

    return response.json()
  },

  /**
   * Get all versions of a prompt.
   */
  async getPromptVersions(promptId: string): Promise<PromptVersionsResponse> {
    const headers = await getAuthHeaders()
    const encodedId = encodeURIComponent(promptId)

    const response = await fetch(`${API_URL}/prompts/${encodedId}/versions`, {
      method: 'GET',
      headers,
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(errorData.error || `Failed to get versions: ${response.status}`)
    }

    return response.json()
  },

  /**
   * Create a new version of a prompt.
   */
  async createPromptVersion(
    promptId: string,
    request: CreatePromptVersionRequest
  ): Promise<PromptResponse> {
    const headers = await getAuthHeaders()
    const encodedId = encodeURIComponent(promptId)

    const response = await fetch(`${API_URL}/prompts/${encodedId}/versions`, {
      method: 'POST',
      headers,
      body: JSON.stringify(request),
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(errorData.error || `Failed to create version: ${response.status}`)
    }

    return response.json()
  },

  /**
   * Activate a specific version of a prompt.
   */
  async activatePromptVersion(promptId: string, version: number): Promise<void> {
    const headers = await getAuthHeaders()
    const encodedId = encodeURIComponent(promptId)

    const response = await fetch(`${API_URL}/prompts/${encodedId}/activate/${version}`, {
      method: 'PUT',
      headers,
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(errorData.error || `Failed to activate version: ${response.status}`)
    }
  },

  /**
   * Test a prompt with sample variables.
   */
  async testPrompt(promptId: string, request: TestPromptRequest): Promise<TestPromptResponse> {
    const headers = await getAuthHeaders()
    const encodedId = encodeURIComponent(promptId)

    const response = await fetch(`${API_URL}/prompts/${encodedId}/test`, {
      method: 'POST',
      headers,
      body: JSON.stringify(request),
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(errorData.error || `Failed to test prompt: ${response.status}`)
    }

    return response.json()
  },

  /**
   * List recent executions.
   */
  async listExecutions(limit?: number): Promise<ExecutionListResponse> {
    const headers = await getAuthHeaders()
    const params = limit ? `?limit=${limit}` : ''

    const response = await fetch(`${API_URL}/prompts/executions${params}`, {
      method: 'GET',
      headers,
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(errorData.error || `Failed to list executions: ${response.status}`)
    }

    return response.json()
  },

  /**
   * Get a specific execution.
   */
  async getExecution(executionId: string): Promise<ExecutionResponse> {
    const headers = await getAuthHeaders()

    const response = await fetch(`${API_URL}/prompts/executions/${executionId}`, {
      method: 'GET',
      headers,
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(errorData.error || `Failed to get execution: ${response.status}`)
    }

    return response.json()
  },

  /**
   * List executions for a specific prompt.
   */
  async listPromptExecutions(promptId: string, limit?: number): Promise<ExecutionListResponse> {
    const headers = await getAuthHeaders()
    const encodedId = encodeURIComponent(promptId)
    const params = limit ? `?limit=${limit}` : ''

    const response = await fetch(`${API_URL}/prompts/${encodedId}/executions${params}`, {
      method: 'GET',
      headers,
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(errorData.error || `Failed to list prompt executions: ${response.status}`)
    }

    return response.json()
  },

  /**
   * List executions for a specific job.
   */
  async listJobExecutions(jobId: string): Promise<ExecutionListResponse> {
    const headers = await getAuthHeaders()

    const response = await fetch(`${API_URL}/jobs/${jobId}/executions`, {
      method: 'GET',
      headers,
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(errorData.error || `Failed to list job executions: ${response.status}`)
    }

    return response.json()
  },
}
